import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Users2, Landmark, HeartHandshake } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Toranam } from "@/components/Ornaments";
import TempleCard from "@/components/TempleCard";
import ArchakaCard from "@/components/ArchakaCard";

const HERO = "https://images.pexels.com/photos/11596291/pexels-photo-11596291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEITIES = [
  { name: "శ్రీ వేంకటేశ్వరుడు", url: "https://images.pexels.com/photos/11596291/pexels-photo-11596291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "శ్రీ శివుడు", url: "https://images.unsplash.com/photo-1548204590-bb618239eea5?crop=entropy&cs=srgb&fm=jpg&w=940&q=85" },
  { name: "శ్రీ గణేశుడు", url: "https://images.pexels.com/photos/28288479/pexels-photo-28288479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "దీప ఆరాధన", url: "https://images.unsplash.com/photo-1780318565569-b9208f729546?crop=entropy&cs=srgb&fm=jpg&w=940&q=85" },
];

export default function Home() {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [temples, setTemples] = useState([]);
  const [archakas, setArchakas] = useState([]);

  useEffect(() => {
    api.get("/temples").then(({ data }) => setTemples(data.slice(0, 3))).catch(() => {});
    api.get("/archakas").then(({ data }) => setArchakas(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div className="parchment-texture">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0"><img src={HERO} alt="temple" className="w-full h-full object-cover" /><div className="absolute inset-0 hero-overlay" /></div>
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-marigold/90 text-maroon text-xs font-semibold font-telugu mb-6 animate-fade-up">
            🪷 సర్వం శ్రీ వేంకటేశ్వరార్పణమస్తు
          </div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white drop-shadow-lg animate-fade-up" style={{ animationDelay: ".1s" }}>
            {t("site_name")}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/95 font-telugu max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: ".2s" }}>
            {t("hero_title")}
          </p>
          <p className="mt-2 text-sm text-white/80 font-telugu animate-fade-up" style={{ animationDelay: ".25s" }}>{t("hero_sub")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 animate-fade-up" style={{ animationDelay: ".3s" }}>
            <Button data-testid="hero-temples" onClick={() => nav("/temples")} className="btn-sacred border-0 px-6 py-6 text-base font-telugu">
              <Landmark className="w-5 h-5 mr-2" />{t("view_temples")}
            </Button>
            <Button data-testid="hero-archakas" onClick={() => nav("/archakas")} variant="outline" className="px-6 py-6 text-base font-telugu bg-white/90 border-maroon text-maroon hover:bg-white">
              <Users2 className="w-5 h-5 mr-2" />{t("view_archakas")}
            </Button>
          </div>
        </div>
      </section>

      <Toranam className="-mt-1" />

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[[temples.length ? "6+" : "6+", "దేవాలయ ఖాళీలు", "🛕"], ["4+", "ఆమోదిత అర్చకులు", "🧎"], ["5", "ఉచిత క్రెడిట్స్", "🎁"], ["100%", "ధృవీకరణ", "✅"]].map(([n, l, e], i) => (
          <div key={i} className="text-center p-5 rounded-2xl bg-surface gold-border temple-shadow">
            <div className="text-3xl">{e}</div>
            <div className="text-2xl font-bold text-saffron mt-1">{n}</div>
            <div className="text-xs text-maroon font-telugu">{l}</div>
          </div>
        ))}
      </section>

      {/* Deity artwork strip */}
      <section className="max-w-6xl mx-auto px-6 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DEITIES.map((d, i) => (
            <div key={i} className="relative h-40 rounded-2xl overflow-hidden gold-border group">
              <img src={d.url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-maroon/90 to-transparent p-2">
                <span className="text-white text-xs font-telugu font-semibold">{d.name}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured temples */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-maroon">🛕 {t("temples_heading")}</h2>
          <Button data-testid="home-more-temples" variant="ghost" onClick={() => nav("/temples")} className="text-saffron font-telugu">అన్నీ చూడండి <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{temples.map((tm) => <TempleCard key={tm.id} temple={tm} />)}</div>
      </section>

      <Toranam />

      {/* Featured archakas */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-maroon">🧎 {t("archakas_heading")}</h2>
          <Button data-testid="home-more-archakas" variant="ghost" onClick={() => nav("/archakas")} className="text-saffron font-telugu">అన్నీ చూడండి <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{archakas.map((a) => <ArchakaCard key={a.id} archaka={a} />)}</div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-maroon text-center mb-10">ఇది ఎలా పనిచేస్తుంది?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[[ShieldCheck, "ధృవీకరించబడిన అర్చకులు", "ప్రతి అర్చక ప్రొఫైల్ అడ్మిన్ ఆమోదం తర్వాతే బహిరంగం. గోత్రం, ఆగమ ధృవీకరణ."],
            [Sparkles, "5 ఉచిత క్రెడిట్స్", "దేవాలయ కమిటీలకు మొదటి 5 అర్చక సంప్రదింపులు పూర్తిగా ఉచితం."],
            [HeartHandshake, "సరైన జోడీ", "కుటుంబం vs బ్యాచిలర్, గోత్రం, జిల్లా ఆధారంగా సరైన అర్చకులను కనుగొనండి."]].map(([Icon, ti, de], i) => (
            <div key={i} className="p-6 rounded-2xl bg-surface gold-border temple-shadow text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-marigold to-saffron flex items-center justify-center mb-4"><Icon className="w-7 h-7 text-white" /></div>
              <h3 className="font-serifte font-bold text-maroon text-lg mb-2">{ti}</h3>
              <p className="text-sm text-muted-foreground font-telugu">{de}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Button data-testid="home-post-cta" onClick={() => nav("/post-vacancy")} className="btn-sacred border-0 px-8 py-6 text-base font-telugu">{t("post_vacancy")} <ArrowRight className="w-5 h-5 ml-2" /></Button>
        </div>
      </section>
    </div>
  );
}
